import {PitchCheck} from "../models/pitch-check";
import {formatDate, fourDays} from "../models/util";
import {
  Game,
  GameList,
  Pitch,
  PitcherLog,
  PitchHistory,
  Player,
  PlayerWithGame,
  TeamInfo,
  ThrowHand
} from "../models/utilTypes";
import {UserRole} from "../models/fixture/userRole";
import {Position} from "../models/position";
import {ITeamRepository, TeamRepository} from "../infrastructure/reposirory/teamRepository";

interface ITeamService {
  info(teamId:string): Promise<TeamInfo>;
  games(teamId:string): Promise<Game[]>;
  players(teamId:string): Promise<Player[]>;
  createGameList(games:Game[], date:Date): GameList[];
  createPlayerList(players: Player[], games:Game[]): PlayerWithGame[];
  pitchersStatus(playerList:PlayerWithGame[], games:Game[], date:Date): PitcherLog[];
  addPlayer(teamId:string, name:string, birth:string, position:Position[], throwHand: ThrowHand, userRole: UserRole): Promise<Player>;
  updatePlayer(playerId: string, newPlayerData: Player, myTeamId:string, userRole: UserRole): Promise<void>;
  deletePlayer(playerId: string, myTeamId:string, userRole: UserRole): Promise<void>;
  addGame(teamId:string, vsTeam: string, dateTime: string, pitches: Pitch[], userRole: UserRole): Promise<Game>;
  updateGame(gameId: string, newGameInfo: Game, myTeamId:string, userRole: UserRole): Promise<void>;
  deleteGame(gameId: string, myTeamId:string, userRole: UserRole): Promise<void>;
}

export class TeamService implements ITeamService{

  private readonly pitchCheck: PitchCheck;
  private readonly _teamRepository: ITeamRepository;

  private static _instance: TeamService;

  private constructor () {
    this.pitchCheck = PitchCheck.instance;
    this._teamRepository = TeamRepository.instance;
  }

  static get instance() {
    if(!this._instance) {
      this._instance = new TeamService();
    }
    return this._instance;
  }

  // teamIDは場合によって参加チームの変更などで変わることがあるため、
  // 引数で持ってくるようにして、サービスやインスタンスに保持させないように作っている
  async info(teamId: string): Promise<TeamInfo> {
    if(teamId) {
      return await this._teamRepository.info(teamId);
    } else {
      throw new Error('チームに所属していません');
    }
  }
  
  async games(teamId:string) {
    if(teamId){
      return await this._teamRepository.games(teamId);
    } else {
      throw new Error('チームに所属していません');
    }
  }
  
  async players(teamId:string) {
    if(teamId){
      return await this._teamRepository.players(teamId);
    } else {
      throw new Error('チームに所属していません');
    }
  }
  
  createGameList(games: Game[], date:Date): GameList[] {
    const targetDay = formatDate(date);
    const gameList: Game[] = this.pitchCheck.gameFourDays(games, targetDay); // 該当日含む4日間の試合リストを作る
    const gameDaysList: string[] = fourDays(targetDay); // 中身は当日→3日前の配列
    return this.pitchCheck.createGameList(gameDaysList, gameList);
  }

  createPlayerList(players: Player[], games:Game[]){
    return this.pitchCheck.createPlayerList(players, games);
  }
  
  pitchersStatus(playerList: PlayerWithGame[], games: Game[], date:Date) {
    const targetDay = formatDate(date);
    // 投球可能か判断したpitchAbleはここで作っている
    const pitchHistories: PitchHistory[] = playerList.map(player => {
      const pitchAble = this.pitchCheck.pitchAble(player, targetDay);
      return this.pitchCheck.pitcherFourDays(pitchAble, targetDay)
    });
    
    // データの整形。数値の書き換えは行っていない
    const gameList: Game[] = this.pitchCheck.gameFourDays(games, targetDay); // 該当日含む4日間の試合リストを作る
    const gameDaysList: string[] = fourDays(targetDay); // 中身は当日→3日前の配列
    const dayList = this.pitchCheck.createGameList(gameDaysList, gameList);
    return this.pitchCheck.createPitchersLog(pitchHistories, dayList);
  }
  
  async addPlayer(teamId:string, name:string, birth:string, position:Position[], throwHand: ThrowHand, userRole: UserRole): Promise<Player> {
    if (teamId) {
      if(userRole === UserRole.owner || userRole === UserRole.member) {
        return await this._teamRepository.addPlayer(teamId, name, birth, position, throwHand);
      } else {
        throw new Error('登録権限がありません');
      }
    } else {
      throw new Error('チームに所属していません');
    }
  }

  async updatePlayer(playerId: string, newPlayerData: Player, myTeamId:string, userRole: UserRole): Promise<void> {
    if (myTeamId) {
      if(userRole === UserRole.owner || userRole === UserRole.member) {
        await this._teamRepository.updatePlayer(newPlayerData, myTeamId);
      } else {
        throw new Error('登録権限がありません');
      }
    } else {
      throw new Error('チームに所属していません');
    }
  }

  async deletePlayer(playerId: string, myTeamId:string, userRole: UserRole): Promise<void> {
    if (myTeamId) {
      if (userRole === UserRole.owner || userRole === UserRole.member) {
        await this._teamRepository.deletePlayer(playerId, myTeamId);
      } else {
        throw new Error('編集権限がありません');
      }
    } else {
      throw new Error('チームに所属していません');
    }
  }

  // // todo:審判の分岐を考えること
  async addGame(teamId:string, vsTeam: string, dateTime: string, pitches: Pitch[], userRole: UserRole): Promise<Game> {
    if(teamId){
      if (userRole === UserRole.owner || userRole === UserRole.member) {
        return await this._teamRepository.addGame(teamId, vsTeam, dateTime, pitches);
      } else {
        throw new Error('編集権限がありません');
      }
    } else {
      throw new Error('チームに所属していません');
    }
  }

  async updateGame(gameId: string, newGameInfo: Game, myTeamId:string, userRole: UserRole): Promise<void> {
    if(myTeamId){
      if (userRole === UserRole.owner || userRole === UserRole.member) {
        await this._teamRepository.updateGame(newGameInfo, myTeamId);
      } else {
        throw new Error('チームに所属していません');
      }
    } else {
      throw new Error('チームに所属していません');
    }
  }

  async deleteGame(gameId: string, myTeamId:string, userRole: UserRole): Promise<void> {
    if(myTeamId){
      if (userRole === UserRole.owner || userRole === UserRole.member) {
        await this._teamRepository.deleteGame(gameId, myTeamId);
      } else {
        throw new Error('チームに所属していません');
      }
    } else {
      throw new Error('チームに所属していません');
    }
  }
  
  
  async getAll(){
    await this._teamRepository.getAll();
  }
}
